import { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';

export type CentralOcrAssociationOption = {
  localAssId: number;
  localAss: string;
};

type Props = {
  open: boolean;
  associations: CentralOcrAssociationOption[];
  selectedIds: number[];
  onClose: () => void;
  onConfirm: (selectedIds: number[]) => void;
};

export const CentralOcrAssociationsDialog = (props: Props) => {
  const { open, associations, selectedIds, onClose, onConfirm } = props;
  const { t } = useTranslation(['association', 'common']);

  const [localSelected, setLocalSelected] = useState<number[]>(selectedIds);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedIds);
      setFilter('');
    }
  }, [open, selectedIds]);

  const toggle = (id: number) => {
    setLocalSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const filtered = filter ? associations.filter((a) => a.localAss.toLowerCase().includes(filter.toLowerCase())) : associations;

  const allFilteredSelected = filtered.length > 0 && filtered.every((a) => localSelected.includes(a.localAssId));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setLocalSelected((prev) => prev.filter((id) => !filtered.some((a) => a.localAssId === id)));
    } else {
      setLocalSelected((prev) => Array.from(new Set([...prev, ...filtered.map((a) => a.localAssId)])));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 600 }}>
        {t('association:centralOcrDialogTitle')}
        <DialogCloseButton handleClose={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          size='small'
          fullWidth
          placeholder={t('common:button.search') as string}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            {t('association:centralOcrSelectedCount', { count: localSelected.length, total: associations.length })}
          </Typography>
          <Button size='small' onClick={toggleAll}>
            {allFilteredSelected ? t('association:centralOcrUnselectAll') : t('association:centralOcrSelectAll')}
          </Button>
        </Box>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}>
          {filtered.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
              {t('association:centralOcrNoMatches')}
            </Typography>
          )}
          {filtered.map((a) => (
            <FormControlLabel
              key={a.localAssId}
              sx={{ display: 'flex', m: 0 }}
              control={<Checkbox size='small' checked={localSelected.includes(a.localAssId)} onChange={() => toggle(a.localAssId)} />}
              label={a.localAss}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          {t('common:button.cancel')}
        </Button>
        <Button variant='contained' disableElevation onClick={() => onConfirm(localSelected)}>
          {t('common:button.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
